// R109 (l): Slack admin surfaces.
//   • slackConnectionRow() + slackNotificationsCard() + mountSlackSettings():
//     the "Slack" row in Admin → Formulas & system → API Connections and the
//     per-event routing card (event | on | channel | DM | Test).
//   • mountSlackPeople(): the Users page's Slack column + matching card
//     (who resolves to which Slack member; manual pick when emails differ).
// Backed by /api/slack/* (server/slack.js). No secret ever reaches the client —
// /status returns booleans, the workspace name and missing scopes only.

const esc = (s: unknown): string => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
const getJson = async (url: string): Promise<{ status: number; body: any }> => {
  try { const r = await fetch(url); return { status: r.status, body: await r.json().catch(() => null) }; }
  catch (e: any) { return { status: 0, body: { error: e && e.message } }; }
};
const sendJson = async (method: string, url: string, payload: unknown): Promise<{ status: number; body: any }> => {
  try {
    const r = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    return { status: r.status, body: await r.json().catch(() => null) };
  } catch (e: any) { return { status: 0, body: { error: e && e.message } }; }
};

const REASONS: Record<string, string> = {
  slack_not_configured: 'Slack app not connected yet',
  not_in_slack: 'email not found in Slack',
  no_email_on_file: 'no email on file',
  ambiguous_name: 'name matches several Slack members',
  name_not_on_roster: 'name not on any roster',
  slack_account_deactivated: 'Slack account deactivated',
  missing_scope: 'Slack app is missing a scope',
  invalid_auth: 'Slack token rejected',
};
const reasonLabel = (r: string | null | undefined): string => (r ? (REASONS[r] || r) : 'not matched');

// ── API Connections row ────────────────────────────────────────────────────
export function slackConnectionRow(): string {
  return `
    <div class="cfg-row" id="cfg-slack-row">
      <div style="min-width:0; flex:1;">
        <div class="cfg-row-lbl">Slack</div>
        <div class="cfg-row-desc" id="cfg-slack-desc" style="overflow-wrap:anywhere;">Checking…</div>
      </div>
      <div class="cfg-row-ctrl" id="cfg-slack-ctrl">
        <span class="cfg-status-dot off"></span>
        <span class="pill pill-gray">…</span>
      </div>
    </div>`;
}

// ── routing card ───────────────────────────────────────────────────────────
export function slackNotificationsCard(): string {
  return `
    <div class="cfg-card" style="margin-bottom:16px;" id="cfg-slack-card">
      <div class="cfg-card-hdr">
        <div class="cfg-icon" style="background:#f5f3ff;">💬</div>
        <div style="flex:1; min-width:0;">
          <div class="cfg-card-title">Slack notifications</div>
          <div class="cfg-card-desc">Pick a channel per event, and whether the people involved get a DM. With no channel, an event uses the old webhook channel (when it has one) — never both.</div>
        </div>
        <button class="btn btn-sm btn-secondary" id="slack-save" disabled>Save</button>
      </div>
      <div class="cfg-card-body" id="slack-card-body" style="padding-top:10px;">
        <div style="font-size:12px; color:var(--text-muted); padding:8px 0;">Loading…</div>
      </div>
    </div>`;
}

type SlackEvent = { key: string; label: string; group: string; dm: string | null; channel_only: boolean; webhook: boolean; suggested: string; note: string; route: { on: boolean; channel: string; dm: boolean } };

export async function mountSlackSettings(): Promise<void> {
  const [st, cfg] = await Promise.all([getJson('/api/slack/status'), getJson('/api/slack/config')]);
  renderStatusRow(st);
  const body = document.getElementById('slack-card-body');
  const saveBtn = document.getElementById('slack-save') as HTMLButtonElement | null;
  if (!body) return;
  if (cfg.status === 403) { body.innerHTML = `<div style="font-size:12px; color:var(--text-muted); padding:8px 0;">🔒 Slack routing is managed by Admin / Director / Manager.</div>`; return; }
  if (cfg.status !== 200 || !cfg.body || !Array.isArray(cfg.body.events)) { body.innerHTML = `<div style="font-size:12px; color:var(--danger-text, #b91c1c); padding:8px 0;">Couldn't load the Slack routing (${esc(cfg.body && cfg.body.error || `HTTP ${cfg.status}`)}).</div>`; return; }
  const events: SlackEvent[] = cfg.body.events;
  const bot = !!cfg.body.bot;
  const webhook = !!cfg.body.webhook_fallback;
  let channels: Array<{ id: string; name: string; is_private: boolean; is_member: boolean }> = [];
  let chanErr = '';
  if (bot) {
    const ch = await getJson('/api/slack/channels');
    if (ch.body && ch.body.ok) channels = ch.body.channels || [];
    else chanErr = (ch.body && ch.body.error) || `HTTP ${ch.status}`;
  }
  const status = st.body || {};
  const recent: any[] = Array.isArray(status.recent) ? status.recent : [];

  const chanOptions = (ev: SlackEvent): string => {
    const none = ev.webhook && webhook ? '— webhook channel (fallback) —' : '— no channel —';
    const known = new Set(channels.map(c => c.id));
    const cur = ev.route.channel;
    return `<option value="">${none}</option>`
      + (cur && !known.has(cur) ? `<option value="${esc(cur)}" selected>${esc(cur)} (not listed)</option>` : '')
      + channels.map(c => `<option value="${esc(c.id)}" ${c.id === cur ? 'selected' : ''}>${c.is_private ? '🔒' : '#'}${esc(c.name)}${c.is_private && !c.is_member ? ' (invite the bot)' : ''}</option>`).join('');
  };
  const groups: string[] = [];
  for (const ev of events) if (!groups.includes(ev.group)) groups.push(ev.group);
  const th = 'padding:6px 8px; font-size:10.5px; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); text-align:left; font-weight:600;';
  const td = 'padding:7px 8px; border-top:1px solid #f1f5f9; font-size:12px; vertical-align:middle;';
  const rows = groups.map(g => `
      <tr><td colspan="5" style="padding:12px 8px 4px; font-size:11px; font-weight:700; color:var(--text);">${esc(g)}</td></tr>
      ${events.filter(e => e.group === g).map(ev => `
      <tr data-ev="${esc(ev.key)}">
        <td style="${td}">
          <div>${esc(ev.label)}</div>
          ${ev.note ? `<div style="font-size:10.5px; color:var(--text-muted);">${esc(ev.note)}</div>` : ''}
          ${!ev.route.channel && ev.suggested && bot ? `<div style="font-size:10.5px; color:var(--text-faint, #94a3b8);">suggested: ${esc(ev.suggested)}</div>` : ''}
        </td>
        <td style="${td} text-align:center;">${ev.channel_only ? '<span title="On/off lives in the owning module" style="color:var(--text-muted);">—</span>' : `<input type="checkbox" class="sl-on" ${ev.route.on ? 'checked' : ''} style="accent-color:var(--accent);">`}</td>
        <td style="${td}"><select class="sl-ch" ${bot ? '' : 'disabled title="Connect the Slack app first (SLACK_BOT_TOKEN in Railway)"'} style="max-width:230px; padding:4px 6px; border:1px solid var(--border); border-radius:6px; font-size:12px; background:#fff;">${chanOptions(ev)}</select></td>
        <td style="${td} text-align:center;">${ev.dm ? `<label title="DM ${esc(ev.dm)}" style="display:inline-flex; gap:4px; align-items:center; font-size:11px; color:var(--text-muted); cursor:pointer;"><input type="checkbox" class="sl-dm" ${ev.route.dm ? 'checked' : ''} ${bot ? '' : 'disabled'} style="accent-color:var(--accent);">${esc(ev.dm)}</label>` : '<span style="color:var(--text-muted);">—</span>'}</td>
        <td style="${td} text-align:right; white-space:nowrap;"><button class="btn btn-sm btn-outline sl-test" ${bot || webhook ? '' : 'disabled'}>Test</button><div class="sl-res" style="font-size:10.5px; margin-top:2px;"></div></td>
      </tr>`).join('')}`).join('');

  body.innerHTML = `
    ${!bot ? `<div style="font-size:12px; background:#fffbeb; border:1px solid #fde68a; color:#92400e; border-radius:8px; padding:9px 12px; margin:6px 0 10px;">The Slack app isn't connected yet — ${webhook ? 'every event that has a webhook fallback still posts to the old webhook channel; channel routing and DMs turn on once' : 'nothing posts to Slack until'} <code>SLACK_BOT_TOKEN</code> ${webhook ? 'is' : '(or <code>SLACK_WEBHOOK_URL</code>) is'} set in Railway.</div>` : ''}
    ${chanErr ? `<div style="font-size:12px; color:var(--danger-text, #b91c1c); margin:6px 0;">Couldn't list channels: ${esc(reasonLabel(chanErr))}</div>` : ''}
    <div style="overflow-x:auto;">
      <table style="width:100%; border-collapse:collapse; min-width:620px;">
        <thead><tr><th style="${th}">Event</th><th style="${th} text-align:center;">On</th><th style="${th}">Channel</th><th style="${th} text-align:center;">DM</th><th style="${th}"></th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <div style="display:flex; justify-content:space-between; align-items:center; gap:10px; margin-top:10px; flex-wrap:wrap;">
      <div id="slack-save-status" style="font-size:11.5px; color:var(--text-muted);">${cfg.body.updated_at ? `Last saved ${esc(new Date(cfg.body.updated_at).toLocaleString())}${cfg.body.updated_by ? ` by ${esc(cfg.body.updated_by)}` : ''}` : 'Using defaults — nothing saved yet.'}</div>
      ${recent.length ? `<details style="font-size:11.5px;"><summary style="cursor:pointer; color:var(--text-muted);">Recent deliveries (${recent.length})</summary>
        <div style="max-height:220px; overflow:auto; margin-top:6px;">${recent.slice(0, 25).map(r => `<div style="padding:2px 0;"><span style="color:var(--text-muted);">${esc(new Date(r.at).toLocaleTimeString())}</span> · ${esc(r.event)} · ${esc(r.via)} · ${r.ok ? '<span style="color:#15803d;">ok</span>' : `<span style="color:#b91c1c;">${esc(r.error || 'skipped')}</span>`}</div>`).join('')}</div></details>` : ''}
    </div>`;

  const dirty = () => { if (saveBtn) saveBtn.disabled = false; const s = document.getElementById('slack-save-status'); if (s) s.textContent = 'Unsaved changes'; };
  body.querySelectorAll('input, select').forEach(el => el.addEventListener('change', dirty));
  if (saveBtn) {
    saveBtn.onclick = async () => {
      const routes: Record<string, any> = {};
      body.querySelectorAll<HTMLTableRowElement>('tr[data-ev]').forEach(tr => {
        const key = tr.dataset.ev!;
        const r: any = {};
        const on = tr.querySelector<HTMLInputElement>('.sl-on'); if (on) r.on = on.checked;
        const ch = tr.querySelector<HTMLSelectElement>('.sl-ch'); if (ch && !ch.disabled) r.channel = ch.value;
        const dm = tr.querySelector<HTMLInputElement>('.sl-dm'); if (dm && !dm.disabled) r.dm = dm.checked;
        routes[key] = r;
      });
      saveBtn.disabled = true; saveBtn.textContent = 'Saving…';
      const out = await sendJson('PUT', '/api/slack/config', { routes });
      saveBtn.textContent = 'Save';
      const s = document.getElementById('slack-save-status');
      if (out.status === 200) { if (s) s.textContent = '✓ Saved'; }
      else { saveBtn.disabled = false; if (s) s.textContent = `Save failed — ${(out.body && out.body.error) || `HTTP ${out.status}`}`; }
    };
  }
  body.querySelectorAll<HTMLButtonElement>('.sl-test').forEach(btn => btn.addEventListener('click', async () => {
    const tr = btn.closest('tr') as HTMLTableRowElement;
    const res = tr.querySelector('.sl-res') as HTMLElement;
    const ch = tr.querySelector<HTMLSelectElement>('.sl-ch');
    btn.disabled = true; res.textContent = '…'; res.style.color = 'var(--text-muted)';
    const out = await sendJson('POST', '/api/slack/test', { event: tr.dataset.ev, ...(ch && !ch.disabled ? { channel: ch.value } : {}) });
    btn.disabled = false;
    const b = out.body || {};
    if (b.ok) { res.textContent = `✓ sent (${b.via === 'bot' ? 'channel' : b.via})`; res.style.color = '#15803d'; }
    else { res.textContent = b.skipped === 'no_route' ? 'no channel / fallback' : b.skipped === 'not_configured' ? 'Slack not connected' : `✗ ${reasonLabel(b.error || `HTTP ${out.status}`)}`; res.style.color = '#b91c1c'; }
  }));
}

function renderStatusRow(st: { status: number; body: any }): void {
  const desc = document.getElementById('cfg-slack-desc');
  const ctrl = document.getElementById('cfg-slack-ctrl');
  if (!desc || !ctrl) return;
  const pill = (cls: string, text: string, on: boolean) => `<span class="cfg-status-dot ${on ? 'on' : 'off'}"></span><span class="pill ${cls}">${esc(text)}</span>`;
  if (st.status === 403) { desc.textContent = 'Office roles only'; ctrl.innerHTML = pill('pill-gray', 'Hidden', false); return; }
  const s = st.body;
  if (st.status !== 200 || !s || !s.bot) { desc.textContent = `Status unavailable${s && s.error ? ` — ${s.error}` : ''}`; ctrl.innerHTML = pill('pill-gray', 'Unknown', false); return; }
  const b = s.bot;
  const parts: string[] = [];
  if (b.ok) {
    parts.push(`Workspace <b>${esc(b.team)}</b>`);
    if (b.missing_scopes && b.missing_scopes.length) parts.push(`<span style="color:#b45309;">missing scopes: ${b.missing_scopes.map(esc).join(', ')} — add them, then reinstall the app</span>`);
    if (!s.signing_secret) parts.push('<span style="color:#b45309;">replies off — set SLACK_SIGNING_SECRET</span>');
    if (s.team_mismatch) parts.push('<span style="color:#b91c1c;">SLACK_TEAM_ID doesn\'t match this token\'s workspace</span>');
  } else if (b.configured) parts.push(`<span style="color:#b91c1c;">token rejected: ${esc(reasonLabel(b.error))}</span>`);
  else parts.push('Bot not connected — set SLACK_BOT_TOKEN + SLACK_SIGNING_SECRET in Railway');
  parts.push(s.webhook_fallback ? 'webhook fallback on' : 'no webhook fallback');
  if (s.signing_secret) parts.push(`events URL <code style="font-size:10.5px;">${esc(s.events_url)}</code>`);
  desc.innerHTML = parts.join(' · ');
  ctrl.innerHTML = b.ok
    ? pill(b.missing_scopes && b.missing_scopes.length ? 'pill-amber' : 'pill-green', b.missing_scopes && b.missing_scopes.length ? 'Needs scopes' : 'Connected', true)
    : b.configured ? pill('pill-red', 'Error', false) : pill(s.webhook_fallback ? 'pill-amber' : 'pill-gray', s.webhook_fallback ? 'Webhook only' : 'Not configured', !!s.webhook_fallback);
}

// ── Users page: Slack column + matching card ───────────────────────────────
export async function mountSlackPeople(root: HTMLElement): Promise<void> {
  const card = root.querySelector<HTMLElement>('#us-slack-card');
  const chips = root.querySelectorAll<HTMLElement>('.us-slack[data-email]');
  const res = await getJson('/api/slack/people');
  if (res.status !== 200 || !res.body || !Array.isArray(res.body.people)) {
    chips.forEach(c => { c.innerHTML = ''; });
    if (card) card.innerHTML = res.status === 403 ? '' : `<div class="pq-card" style="margin-top:18px;"><div style="padding:14px 22px; font-size:12.5px; color:var(--text-muted);">Slack matching unavailable (${esc((res.body && res.body.error) || `HTTP ${res.status}`)}).</div></div>`;
    return;
  }
  const data = res.body;
  const people: any[] = data.people;
  const byEmail = new Map<string, any>(people.filter(p => p.email).map(p => [p.email, p]));
  const chip = (p: any) => p && p.slack
    ? `<span class="pill pill-green" title="Slack: ${esc(p.slack.display)} (matched by ${esc(p.source)})" style="font-size:10.5px;">Slack ✓ ${esc(p.slack.display)}</span>`
    : `<span class="pill pill-gray" title="${esc(reasonLabel(p && p.reason))} — pick their Slack account in the Slack card below" style="font-size:10.5px; cursor:pointer;" data-goto-slack="1">Slack ✗</span>`;
  chips.forEach(c => {
    const p = byEmail.get(String(c.dataset.email || '').toLowerCase());
    c.innerHTML = data.configured ? chip(p) : '';
    c.querySelector('[data-goto-slack]')?.addEventListener('click', () => card?.scrollIntoView({ behavior: 'smooth' }));
  });
  if (!card) return;
  let slackUsers: any[] | null = null;
  const loadUsers = async () => {
    if (slackUsers) return slackUsers;
    const u = await getJson('/api/slack/users');
    slackUsers = u.body && u.body.ok ? u.body.users : [];
    return slackUsers;
  };
  const rows = people.map((p, i) => `
    <div class="pq-row" style="cursor:default;" data-i="${i}">
      <div class="pq-main">
        <div class="pq-name">${esc(p.name || p.email || '—')}</div>
        <div style="font-size:11px; color:var(--text-muted); margin-top:2px;">${esc(p.email || 'no email')} · ${esc((p.roles || []).join(', '))}</div>
      </div>
      <div class="pq-right" style="gap:8px; flex-wrap:wrap; justify-content:flex-end;">
        ${p.slack
          ? `<span class="pill pill-green" style="font-size:11px;">✓ ${esc(p.slack.display)}${p.slack.guest ? ' (guest)' : ''}</span><span style="font-size:10.5px; color:var(--text-muted);">${p.source === 'manual' ? 'picked manually' : p.source === 'email' ? 'by email' : 'by name'}</span>`
          : `<span class="pill pill-gray" style="font-size:11px;">✗ ${esc(reasonLabel(p.reason))}</span>`}
        ${data.configured ? `<button class="btn btn-sm btn-outline sl-pick" style="font-size:11px;">${p.slack ? 'Change' : 'Pick…'}</button>${p.source === 'manual' ? '<button class="btn btn-sm btn-outline sl-clear" style="font-size:11px;">Clear</button>' : ''}` : ''}
        <span class="sl-pick-host"></span>
      </div>
    </div>`).join('');
  card.innerHTML = `
    <div class="pq-card" style="margin-top:18px;">
      <div class="pq-card-h"><div><h3>Slack</h3><p>${data.configured
        ? `${data.matched} of ${people.length} teammates matched to a Slack member — DMs (assignments, "ask for info", on-call emergencies) reach only matched people. If someone's Slack email differs from their login / roster email, pick them here.`
        : 'Connect the Slack app (SLACK_BOT_TOKEN in Railway) to match teammates to Slack members.'}${data.slack_error && data.configured ? ` <span style="color:#b91c1c;">(${esc(reasonLabel(data.slack_error))})</span>` : ''}</p></div></div>
      ${rows || '<div style="padding:16px 22px; font-size:12.5px; color:var(--text-muted);">No teammates found.</div>'}
    </div>`;
  const overrideKey = (p: any): string => p.override_key || p.key;
  card.querySelectorAll<HTMLButtonElement>('.sl-pick').forEach(btn => btn.addEventListener('click', async () => {
    const row = btn.closest('[data-i]') as HTMLElement;
    const p = people[Number(row.dataset.i)];
    const host = row.querySelector('.sl-pick-host') as HTMLElement;
    host.innerHTML = '<span style="font-size:11px; color:var(--text-muted);">loading…</span>';
    const users = await loadUsers();
    host.innerHTML = `<select style="padding:4px 6px; border:1px solid var(--border); border-radius:6px; font-size:12px; max-width:220px;"><option value="">— pick a Slack member —</option>${users.map(u => `<option value="${esc(u.id)}">${esc(u.real_name || u.name)}${u.display_name && u.display_name !== u.real_name ? ` (@${esc(u.display_name)})` : ''}${u.email ? ` · ${esc(u.email)}` : ''}${u.guest ? ' · guest' : ''}</option>`).join('')}</select> <button class="btn btn-sm btn-secondary" style="font-size:11px;">Save</button>`;
    host.querySelector('button')?.addEventListener('click', async () => {
      const sel = host.querySelector('select') as HTMLSelectElement;
      if (!sel.value) return;
      const out = await sendJson('POST', '/api/slack/people', { key: overrideKey(p), user_id: sel.value });
      if (out.status === 200) await mountSlackPeople(root);
      else host.insertAdjacentHTML('beforeend', `<span style="font-size:11px; color:#b91c1c;"> ${esc((out.body && out.body.error) || `HTTP ${out.status}`)}</span>`);
    });
  }));
  card.querySelectorAll<HTMLButtonElement>('.sl-clear').forEach(btn => btn.addEventListener('click', async () => {
    const p = people[Number((btn.closest('[data-i]') as HTMLElement).dataset.i)];
    const out = await sendJson('POST', '/api/slack/people', { key: overrideKey(p), user_id: null });
    if (out.status === 200) await mountSlackPeople(root);
  }));
}
